import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

import styled, { css } from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import Table from "components/molecules/table/Table";
import {
    compareColumns,
    getColumnOrder,
    getColumnSize,
    getColumnVisibility,
    updateColumnOrder,
} from "components/molecules/table/TableManager";
import ResearchAddModal from "components/page/modal/ResearchAddModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useResearchBulkDelete, useResearchUpdate, useResearches } from "hooks/queries/useResearch";
import { useUser } from "hooks/queries/useUser";
import { useSessionPageHistory } from "hooks/useSessionPageHistory";

import { useDevice } from "utils/device";
import { isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

import moment from "moment/moment";

const HeaderLeftWrap = styled.div`
    display: flex;
    align-items: end;
    gap: 8px;
`;

const HeaderRightWrap = styled.div`
    position: relative;
    margin: 0 0 0 40px;
`;

const BodyHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
`;

const BodyHeaderLeftSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    width: 171px;
    margin-left: 10px;
`;

const BodyHeaderRightSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    margin: 0 0 0 24px;
`;

const SearchWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 12px 0 0;
    width: 100%;
    min-width: 200px;
    max-width: 320px;
`;

const SearchInputBox = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1px 4px;
    width: 100%;
    height: 28px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Line);
    border-radius: 4px;

    &:focus-within {
        border: solid 1px var(--color-Key);
    }
`;

const SearchInput = styled.input`
    padding: 0 8px;
    width: calc(100% - 24px);
    height: 24px;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-Black);
    background-color: transparent;
    border: transparent;
    outline: var(--color-Key);
`;

const SearchButtonWrap = styled.div`
    display: flex;
    align-items: center;
    height: 24px;
`;

const FavoriteWrap = styled.div`
    display: flex;
    align-items: center;
`;

const BlankView = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const AddInfoSection = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: auto;
    margin-bottom: 24px;
`;

const AddInfoBox = styled.div`
    flex: 1;
    display: flex;
    justify-content: space-between;
    padding: 16px;
    width: 100%;
    border-radius: 8px;
    background-color: var(--color-InfoBox);

    ${(props) =>
        props.device.isPC &&
        css`
            padding: 24px;
        `};
`;

const AddInfoItem = styled.div`
    width: 50%;
    max-width: calc(50% - 8px);
`;

const AddInfoItemRow = styled.div`
    display: flex;
    align-items: center;
    margin: 0 0 8px 6px;
    width: 100%;

    &:last-child {
        margin: 0 0 0 6px;
    }
`;

const AddInfoSubItemWrap = styled.div`
    margin: 0 0 8px 32px;
`;

const AddInfoSubItem = styled.div`
    display: flex;
    align-items: center;
`;

const CheckIconStyle = {
    name: "check",
    size: "14",
    color: "var(--color-DarkKey)",
};

const ItemTextStyle = {
    margin: "0 0 0 8px",
    variant: "body-lg-400",
    color: "var(--color-SubBlack)",
};

const SubItemTextStyle = {
    margin: "0 0 4px 4px",
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
};

const defaultSearchParams = {
    page_size: 10,
    page: 1,
    is_favorite: false,
    search: "",
    ordering: "",
};

//region: 컴포넌트 정의 시작
function ResearchListPage(props) {
    const nav = useNavigate();
    const { space_uuid } = useParams();
    const location = useLocation();

    const device = useDevice();
    const { setToast } = useToast();

    const updateResearchMutation = useResearchUpdate();
    const deleteBulkResearchMutation = useResearchBulkDelete();

    const [searchFilter, setSearchFilter] = useState("");
    const [searchParams, setSearchParams] = useState({ ...defaultSearchParams });
    const researchQuery = useResearches(searchParams);
    const [researches, setResearches] = useState({
        count: 0,
        results: [],
    });

    const [addResearchModal, setAddResearchModal] = useState(false);

    const rerenderTable = React.useReducer(() => ({}), {})[1];

    const PAGE_KEY = "researchList";

    useEffect(() => {
        if (location.state) {
            setSearchParams({ ...defaultSearchParams, ...location.state });
        }
    }, [location.state]);

    useEffect(() => {
        return () => {
            researchQuery.remove();
        };
    }, []);

    useEffect(() => {
        if (searchParams) {
            researchQuery.refetch();
        }
    }, [searchParams]);

    // region: 체크박스 선택 동작
    const checkedResearchesRef = useRef([]);

    const handleCheckboxAllChange = (e) => {
        // 전체 선택이면 모든 id를, 그렇지 않으면 빈 배열을 반환
        checkedResearchesRef.current = e.target.checked ? researches?.results?.map((research) => research.id) : [];
        rerenderTable();
    };

    const handleCheckboxChange = (event) => {
        let { id } = event.target;
        id = Number(id);
        handleCheckboxChangeById(id);
    };

    const handleCheckboxChangeById = (id) => {
        const index = checkedResearchesRef.current.indexOf(id);
        if (index > -1) {
            checkedResearchesRef.current = checkedResearchesRef.current.filter((item) => item !== id);
        } else {
            checkedResearchesRef.current = [...checkedResearchesRef.current, id];
        }
        rerenderTable();
    };

    const handleExecuteCheckedResearchesAction = (checkedResearchesAction) => {
        if (isReadonlyUser(user)) {
            setToast("읽기 전용 사용자 계정은 해당 기능을 사용할 수 없습니다.", "warn");
            return;
        }
        if (checkedResearchesAction === "delete") {
            Confirm("warn", "삭제", "선택 항목을 삭제하시겠습니까?", "삭제", (e) => {
                deleteBulkResearchMutation.mutate(
                    { ids: checkedResearchesRef.current },
                    {
                        onSuccess: (data, variables, context) => {
                            setToast("성공적으로 삭제되었습니다.", "info");
                            if (
                                researches.results.length === checkedResearchesRef.current.length &&
                                searchParams.page > 1
                            ) {
                                setSearchParams({
                                    ...searchParams,
                                    page: searchParams.page - 1,
                                });
                            }
                            checkedResearchesRef.current = [];
                        },
                    },
                );
            });
        }
    };

    const [tableConfig, setTableConfig] = useState(
        localStorage.getItem(PAGE_KEY) ? JSON.parse(localStorage.getItem(PAGE_KEY)).research : [],
    );

    useEffect(() => {
        const config = compareColumns(columns, tableConfig);
        setTableConfig(config);
        localStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                research: tableConfig,
            }),
        );
    }, [tableConfig]);

    //region: 테이블 컬럼 정의
    const columns = React.useMemo(
        () => [
            {
                id: "id",
                accessorKey: "id",
                header: null,
                name: "선택",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                content: (info) => {
                    const value = info.getValue();
                    return (
                        <Checkbox
                            id={value}
                            checked={checkedResearchesRef.current.includes(value)}
                            onChange={handleCheckboxChange}
                        />
                    );
                },
            },
            {
                id: "is_favorite",
                accessorKey: "is_favorite",
                header: null,
                name: "즐겨찾기",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                defaultVisibility: true,
                content: (info) => {
                    return (
                        <Button
                            onlyIcon
                            onClick={(e) => {
                                e.stopPropagation();
                                updateResearchMutation.mutate(
                                    {
                                        id: info.row.original.id,
                                        is_favorite: !info.getValue(),
                                    },
                                    {
                                        onSuccess: (data) => {
                                            rerenderTable();
                                        },
                                    },
                                );
                            }}
                            bgColor={"transparent"}
                            hoverBgColor={"transparent"}
                            disabled={isReadonlyUser(user)}
                        >
                            {info.row.original.is_favorite ? (
                                <Icon name={"bookmark"} color={"var(--color-Key)"} size={"16px"} />
                            ) : (
                                <Icon name={"bookmarkDisactive"} size={"16px"} />
                            )}
                        </Button>
                    );
                },
            },
            {
                id: "display_name",
                accessorKey: "display_name",
                name: "제목",
                size: getColumnSize("display_name", tableConfig) || 250,
                text: (info) => {
                    return info.getValue();
                },
                onTextClick: (info) => {
                    saveSessionCache();
                    nav(`/${space_uuid}/research/${info.row.original.research_no}`);
                },
            },
            {
                id: "plain_text_content",
                accessorKey: "plain_text_content",
                name: "내용",
                style: { justifyContent: "left" },
                ...(getColumnSize("plain_text_content", tableConfig) && {
                    size: getColumnSize("plain_text_content", tableConfig),
                }),
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "created_at",
                accessorKey: "created_at",
                name: "생성일시",
                size: getColumnSize("created_at", tableConfig) || 100,
                text: (info) => {
                    return moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss");
                },
            },
            {
                id: "updated_at",
                accessorKey: "updated_at",
                name: "수정일시",
                size: getColumnSize("updated_at", tableConfig) || 100,
                text: (info) => {
                    return moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss");
                },
            },
        ],
        [],
    );

    useEffect(() => {
        if (researchQuery.data) {
            setResearches(researchQuery.data);
        }
    }, [researchQuery.data]);

    const handleToggleFavorite = () => {
        setSearchParams({ ...searchParams, is_favorite: !searchParams.is_favorite, page: 1 });
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ ...searchParams, page: newPage });
    };

    const handlePageSizeChange = (newSize) => {
        setSearchParams({ ...searchParams, page_size: newSize, page: 1 });
    };

    const handleSortChange = (sort) => {
        const sortValue = sort
            .map((sortItem) => {
                return sortItem.desc ? `-${sortItem.id}` : sortItem.id;
            })
            .join(",");
        setSearchParams({ ...searchParams, ordering: sortValue, page: 1 });
    };

    const { prevPage } = useSessionPageHistory();

    // searchParams state 가 업데이트 되지 않은 버그 대응
    const searchParamsRef = useRef({});
    useEffect(() => {
        searchParamsRef.current = { ...searchParams };
    }, [searchParams]);

    const saveSessionCache = () => {
        sessionStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                searchParams: searchParams,
            }),
        );
    };

    const loadSessionCache = () => {
        const sessionCacheJson = sessionStorage.getItem(PAGE_KEY);
        if (sessionCacheJson) {
            const sessionCacheObj = JSON.parse(sessionCacheJson);
            setSearchParams(sessionCacheObj.searchParams);
        }
    };

    const clearSessionCache = () => {
        sessionStorage.removeItem(PAGE_KEY);
    };

    useEffect(() => {
        // 상세 페이지에서 이동해왔을 때 페이지 복원
        if (prevPage && prevPage.startsWith("/research/")) {
            loadSessionCache();
            clearSessionCache(); // 새로고침 했을 때 기본값으로 처리되도록 삭제
            researchQuery.remove();
        }
    }, [prevPage]);

    useEffect(() => {
        // 상세 페이지에서 돌아왔을 때 검색창에 텍스트 자동 입력 처리
        setSearchFilter(searchParams.search);
    }, [searchParams.search]);

    const tableRef = useRef();

    useEffect(() => {
        checkedResearchesRef.current = [];
        rerenderTable();
    }, [researches?.results]);

    const [user, setUser] = useState({});
    const userQuery = useUser();
    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    //region: 페이지 레이아웃 마크업 시작
    return (
        <LoginUserLayout>
            <HtmlHead title={"연구노트"} />
            <Breadcrumb>
                <HeaderLeftWrap>
                    <BreadcrumbTitle>연구노트</BreadcrumbTitle>
                    <CountTagItem>
                        <TagText>{researches.count}건</TagText>
                    </CountTagItem>
                </HeaderLeftWrap>
                <HeaderRightWrap>
                    <Button
                        onClick={() => setAddResearchModal(!addResearchModal)}
                        width={"132px"}
                        height={"32px"}
                        bgColor={"var(--color-Button2)"}
                        hoverBgColor={"var(--color-ButtonHover2)"}
                        border={"solid 2px transparent"}
                        fontColor={"var(--color-White)"}
                        buttonText={"연구노트 추가"}
                        disabled={isReadonlyUser(user)}
                    >
                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                    </Button>
                </HeaderRightWrap>
            </Breadcrumb>

            <BodyWithBreadcrumb>
                {researches?.count !== 0 ? (
                    //region: 연구노트 목록 있을 때
                    <>
                        <BodyHeader>
                            <BodyHeaderLeftSection>
                                <Checkbox
                                    margin={"0 20px 0 0"}
                                    indeterminate={
                                        checkedResearchesRef.current.length > 0 &&
                                        checkedResearchesRef.current.length < researches?.results?.length
                                    }
                                    checked={
                                        researches?.results?.length > 0 &&
                                        checkedResearchesRef.current.length === researches?.results?.length
                                    }
                                    onChange={handleCheckboxAllChange}
                                />
                                <SelectBox
                                    width={"auto"}
                                    height={"28px"}
                                    optionWidth={"200px"}
                                    optionAlign={"left"}
                                    optionMaxHeight={"auto"}
                                    items={[
                                        { id: "", name: "--- 액션을 선택하세요. ---" },
                                        { id: "delete", name: "선택된 항목을 삭제합니다." },
                                    ]}
                                    textAlign={"left"}
                                    value={""}
                                    onChange={(value) => {
                                        if (checkedResearchesRef.current.length === 0) {
                                            setToast("선택된 항목이 없습니다.", "warn");
                                            return;
                                        }
                                        handleExecuteCheckedResearchesAction(value);
                                    }}
                                />
                                {checkedResearchesRef.current.length > 0 && (
                                    <Text margin="0 0 0 10px" variant="body-sm-400" fontWeight="300">
                                        {checkedResearchesRef.current.length}개의 항목이 선택됨
                                    </Text>
                                )}
                            </BodyHeaderLeftSection>
                            <BodyHeaderRightSection>
                                <SearchWrap>
                                    <SearchInputBox>
                                        <SearchInput
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                            onKeyDown={(e) => {
                                                genericEnterKeyDown(e, () => {
                                                    setSearchParams({ ...searchParams, search: searchFilter, page: 1 });
                                                });
                                            }}
                                        />
                                        <SearchButtonWrap>
                                            {searchFilter && (
                                                <Tooltip message={"초기화"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"var(--color-White)"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={(e) => {
                                                            setSearchFilter("");
                                                            setSearchParams({ ...searchParams, search: "", page: 1 });
                                                        }}
                                                    >
                                                        <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            <Tooltip message={"검색"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"var(--color-White)"}
                                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                                    onClick={(e) => {
                                                        setSearchParams({
                                                            ...searchParams,
                                                            search: searchFilter,
                                                            page: 1,
                                                        });
                                                    }}
                                                >
                                                    <Icon name={"search"} size={"12"} color={"var(--color-SubBlack)"} />
                                                </Button>
                                            </Tooltip>
                                        </SearchButtonWrap>
                                    </SearchInputBox>
                                </SearchWrap>
                                <FavoriteWrap>
                                    <Tooltip message={"즐겨찾기만 보기"}>
                                        <Button
                                            onlyIcon
                                            buttonSize={"24px"}
                                            bgColor={"var(--color-White)"}
                                            hoverBgColor={"none"}
                                            border={
                                                searchParams.is_favorite
                                                    ? "solid 2px var(--color-Key)"
                                                    : "solid 2px var(--color-DisabledButton)"
                                            }
                                            onClick={() => handleToggleFavorite()}
                                        >
                                            <Icon
                                                name={"bookmark"}
                                                size={"12px"}
                                                color={
                                                    searchParams.is_favorite
                                                        ? "var(--color-Key)"
                                                        : "var(--color-DisabledButton)"
                                                }
                                            />
                                        </Button>
                                    </Tooltip>
                                </FavoriteWrap>
                            </BodyHeaderRightSection>
                        </BodyHeader>
                        <Table
                            ref={tableRef}
                            data={researches?.results || []}
                            columns={columns}
                            useSizingColumns={true}
                            useSortingColumns={true}
                            useOrderColumns={true}
                            pagination={{
                                pageIndex: searchParams.page,
                                pageSize: searchParams.page_size,
                                count: researches?.count,
                            }}
                            setPagination={(page) => {
                                if (Number(searchParams.page_size) !== Number(page.pageSize)) {
                                    handlePageSizeChange(page.pageSize);
                                } else if (searchParams.page !== Number(page.pageIndex)) {
                                    handlePageChange(page.pageIndex);
                                } else {
                                    return;
                                }
                            }}
                            sorting={
                                searchParams?.ordering
                                    ? [
                                          {
                                              id: searchParams.ordering.replace("-", ""),
                                              desc: searchParams.ordering.startsWith("-"),
                                          },
                                      ]
                                    : []
                            }
                            setSorting={handleSortChange}
                            onRowClick={(row) => {
                                handleCheckboxChangeById(row.original.id);
                            }}
                            onSizeChange={(changedColumn) => {
                                Object.keys(changedColumn).forEach((key) => {
                                    setTableConfig((prev) =>
                                        prev.map((item) =>
                                            item.id === key ? { ...item, size: changedColumn[key] } : item,
                                        ),
                                    );
                                });
                            }}
                            columnVisibility={getColumnVisibility(tableConfig)}
                            columnOrder={getColumnOrder(tableConfig)}
                            setColumnOrder={(oldIndex, newIndex) =>
                                setTableConfig(updateColumnOrder(tableConfig, oldIndex, newIndex))
                            }
                            checkedRowIds={checkedResearchesRef.current}
                        />
                    </>
                ) : searchParams.search || searchParams.is_favorite ? (
                    //region: 검색 결과가 없을 때
                    <>
                        <BodyHeader>
                            <BodyHeaderLeftSection>
                                <Checkbox
                                    margin={"0 20px 0 0"}
                                    indeterminate={
                                        checkedResearchesRef.current.length > 0 &&
                                        checkedResearchesRef.current.length < researches?.results?.length
                                    }
                                    checked={
                                        researches?.results?.length > 0 &&
                                        checkedResearchesRef.current.length === researches?.results?.length
                                    }
                                    onChange={handleCheckboxAllChange}
                                />
                                <SelectBox
                                    width={"auto"}
                                    height={"28px"}
                                    optionWidth={"200px"}
                                    optionAlign={"left"}
                                    optionMaxHeight={"auto"}
                                    items={[
                                        { id: "", name: "--- 액션을 선택하세요. ---" },
                                        { id: "delete", name: "선택된 항목을 삭제합니다." },
                                    ]}
                                    textAlign={"left"}
                                    value={""}
                                    onChange={(value) => {
                                        if (checkedResearchesRef.current.length === 0) {
                                            setToast("선택된 항목이 없습니다.", "warn");
                                            return;
                                        }
                                        handleExecuteCheckedResearchesAction(value);
                                    }}
                                />
                                {checkedResearchesRef.current.length > 0 && (
                                    <Text margin="0 0 0 10px" variant="body-sm-400" fontWeight="300">
                                        {checkedResearchesRef.current.length}개의 항목이 선택됨
                                    </Text>
                                )}
                            </BodyHeaderLeftSection>
                            <BodyHeaderRightSection>
                                <SearchWrap>
                                    <SearchInputBox>
                                        <SearchInput
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                            onKeyDown={(e) => {
                                                genericEnterKeyDown(e, () => {
                                                    setSearchParams({ ...searchParams, search: searchFilter, page: 1 });
                                                });
                                            }}
                                        />
                                        <SearchButtonWrap>
                                            {searchFilter && (
                                                <Tooltip message={"초기화"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"var(--color-White)"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={(e) => {
                                                            setSearchFilter("");
                                                            setSearchParams({ ...searchParams, search: "", page: 1 });
                                                        }}
                                                    >
                                                        <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            <Tooltip message={"검색"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"var(--color-White)"}
                                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                                    onClick={(e) => {
                                                        setSearchParams({
                                                            ...searchParams,
                                                            search: searchFilter,
                                                            page: 1,
                                                        });
                                                    }}
                                                >
                                                    <Icon name={"search"} size={"12"} color={"var(--color-SubBlack)"} />
                                                </Button>
                                            </Tooltip>
                                        </SearchButtonWrap>
                                    </SearchInputBox>
                                </SearchWrap>
                                <FavoriteWrap>
                                    <Tooltip message={"즐겨찾기만 보기"}>
                                        <Button
                                            onlyIcon
                                            buttonSize={"24px"}
                                            bgColor={"var(--color-White)"}
                                            hoverBgColor={"none"}
                                            border={
                                                searchParams.is_favorite
                                                    ? "solid 2px var(--color-Key)"
                                                    : "solid 2px var(--color-DisabledButton)"
                                            }
                                            onClick={() => handleToggleFavorite()}
                                        >
                                            <Icon
                                                name={"bookmark"}
                                                size={"12px"}
                                                color={
                                                    searchParams.is_favorite
                                                        ? "var(--color-Key)"
                                                        : "var(--color-DisabledButton)"
                                                }
                                            />
                                        </Button>
                                    </Tooltip>
                                </FavoriteWrap>
                            </BodyHeaderRightSection>
                        </BodyHeader>
                        <BlankView>
                            <Text variant="body-lg-400" margin="0 0 16px 0">
                                일치하는 연구노트가 없습니다.
                            </Text>
                        </BlankView>
                    </>
                ) : (
                    //region: 연구노트가 없을 때
                    <>
                        <BlankView>
                            <Text variant="body-lg-400" margin="0 0 24px 0">
                                아직 연구노트가 없습니다.
                            </Text>
                            <Text margin="0 0 4px 0" variant="body-lg-400" textAlign="center">
                                <strong>[연구노트 추가]</strong> 버튼을 선택하여 새 연구노트를 추가하세요.
                            </Text>
                        </BlankView>
                        <AddInfoSection>
                            <AddInfoBox device={device}>
                                <AddInfoItem>
                                    <Text margin="0 0 16px 0" variant="body-lg-400" fontWeight="600">
                                        연구노트를 추가하면 다음 기능들을 이용할 수 있습니다.
                                    </Text>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>연구노트 텍스트 작성</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>연구노트 목록 관리, 검색</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>연구노트별 참고문헌 인용 관리</Text>
                                    </AddInfoItemRow>
                                </AddInfoItem>
                                <AddInfoItem>
                                    <Text margin="0 0 16px 0" variant="body-lg-400" fontWeight="600">
                                        연구노트에서 다음의 기능들을 이용할 수 있습니다.
                                    </Text>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>AI 번역, 교정</Text>
                                    </AddInfoItemRow>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>AI 글쓰기 보조 기능</Text>
                                    </AddInfoItemRow>
                                    <AddInfoSubItemWrap>
                                        <AddInfoSubItem>
                                            <Text variant="body-lg-400" color="var(--color-SubBlack)">
                                                &#183;
                                            </Text>
                                            <Text {...SubItemTextStyle}>노트 내용 늘이기, 줄이기, 이어쓰기</Text>
                                        </AddInfoSubItem>
                                    </AddInfoSubItemWrap>
                                    <AddInfoItemRow>
                                        <Icon {...CheckIconStyle} />
                                        <Text {...ItemTextStyle}>노트에 인용문 추가 및 인용스타일 관리</Text>
                                    </AddInfoItemRow>
                                </AddInfoItem>
                            </AddInfoBox>
                        </AddInfoSection>
                    </>
                )}
            </BodyWithBreadcrumb>
            {addResearchModal && (
                <ResearchAddModal
                    exitModal={(e) => setAddResearchModal(!addResearchModal)}
                    onSuccess={(data) => {
                        nav(`/${space_uuid}/research/${data.research_no}`, { state: { type: "create" } });
                    }}
                />
            )}
        </LoginUserLayout>
    );
}

export default ResearchListPage;
